import { Link } from 'react-router-dom'
import { ArrowRight, Clock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import type { ApplicationDocument } from '@/types'
import { formatApplicationDeadline } from '../lib/deadlineFormatting'

interface Props {
  application: ApplicationDocument & { id: string }
}

export function ApplicationCard({ application }: Props) {
  const isClosed = application.status === 'closed'
  const isDraft = application.status === 'draft'
  const isBlocked = isClosed || isDraft

  return (
    <Card className="flex h-full flex-col overflow-hidden transition-shadow hover:shadow-md">
      {/* Image */}
      <div
        className="relative aspect-video w-full overflow-hidden bg-muted"
        style={{
          maskImage:
            'linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to bottom, transparent 0%, black 12%, black 88%, transparent 100%)',
        }}
      >
        {application.imageUrl ? (
          <img
            src={application.imageUrl}
            alt={application.title}
            className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
            style={{
              objectPosition: `${(application.imageFocusX ?? 0.5) * 100}% ${(application.imageFocusY ?? 0.5) * 100}%`,
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/10">
            <span className="text-2xl font-bold text-muted-foreground/30">
              {application.title[0]}
            </span>
          </div>
        )}
        {isBlocked && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-sm">
            <Badge variant={isClosed ? 'destructive' : 'secondary'} className="text-sm">
              {isClosed ? 'Closed' : 'Not Yet Open'}
            </Badge>
          </div>
        )}
      </div>

      <CardContent className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold leading-tight">{application.title}</h3>
          {application.status === 'open' && !isBlocked && (
            <Badge variant="default" className="shrink-0 text-xs">
              Open
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground line-clamp-3">{application.description}</p>

        {application.deadline && !isBlocked && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3 shrink-0" />
            Closes{' '}
            {formatApplicationDeadline(new Date(
              (application.deadline as unknown as { toDate(): Date }).toDate?.() ??
                application.deadline
            ))}
          </p>
        )}
      </CardContent>

      <CardFooter className="p-4 pt-0">
        {isBlocked ? (
          <Button className="w-full" disabled variant="secondary">
            {isClosed ? 'Applications Closed' : 'Not Yet Open'}
          </Button>
        ) : (
          <Button asChild className="w-full">
            <Link to={`/applications/${application.slug}`}>
              Apply Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        )}
      </CardFooter>
    </Card>
  )
}
